import * as React from 'react'
import { createPortal } from 'react-dom'
import { Dropdown } from 'antd'
import { CheckOutlined, RightOutlined } from '@ant-design/icons'
import { cn } from '@/lib/utils'

// ============= DropdownMenu 组件（兼容 Radix API）=============
// 注意：Ant Design Dropdown 的 API 与 Radix 差异较大
// 为保持兼容性，这里使用原生 div + 状态管理实现

interface DropdownMenuContextValue {
  open: boolean
  onOpenChange: (open: boolean) => void
  triggerRef: React.RefObject<HTMLDivElement | null>
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue>({
  open: false,
  onOpenChange: () => {},
  triggerRef: { current: null },
})

interface DropdownMenuProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
}

function DropdownMenu({ open, defaultOpen = false, onOpenChange, children }: DropdownMenuProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const currentOpen = open ?? internalOpen

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen)
      onOpenChange?.(newOpen)
    },
    [onOpenChange]
  )

  return (
    <DropdownMenuContext.Provider value={{ open: currentOpen, onOpenChange: handleOpenChange, triggerRef }}>
      {children}
    </DropdownMenuContext.Provider>
  )
}

// DropdownMenuTrigger
interface DropdownMenuTriggerProps {
  children?: React.ReactNode
  asChild?: boolean
}

function DropdownMenuTrigger({ children, asChild }: DropdownMenuTriggerProps) {
  const { onOpenChange, open, triggerRef } = React.useContext(DropdownMenuContext)

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onOpenChange(!open)
  }

  if (asChild && React.isValidElement(children)) {
    return (
      <div ref={triggerRef} className="inline-flex">
        {React.cloneElement(children as React.ReactElement<any>, {
          onClick: (e: React.MouseEvent) => {
            ;(children as React.ReactElement<any>).props?.onClick?.(e)
            handleClick(e)
          },
        })}
      </div>
    )
  }

  return (
    <div ref={triggerRef} className="inline-flex">
      <button type="button" onClick={handleClick}>
        {children}
      </button>
    </div>
  )
}

// DropdownMenuContent
interface DropdownMenuContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  sideOffset?: number
  align?: 'start' | 'center' | 'end'
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ className, children, sideOffset = 4, align = 'end', ...props }, ref) => {
    const { open, onOpenChange, triggerRef } = React.useContext(DropdownMenuContext)
    const [position, setPosition] = React.useState({ top: 0, left: 0 })
    const contentRef = React.useRef<HTMLDivElement>(null)
    const [mounted, setMounted] = React.useState(false)

    // 确保只在客户端渲染
    React.useEffect(() => {
      setMounted(true)
    }, [])

    // 计算下拉框位置
    React.useEffect(() => {
      if (open && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const contentWidth = contentRef.current?.offsetWidth || 192 // 默认宽度

        let left = rect.left
        if (align === 'end') {
          left = rect.right - contentWidth
        } else if (align === 'center') {
          left = rect.left + (rect.width - contentWidth) / 2
        }

        // 确保不超出右边界
        const maxLeft = window.innerWidth - contentWidth - 8
        left = Math.min(left, maxLeft)
        left = Math.max(8, left)

        setPosition({
          top: rect.bottom + sideOffset,
          left,
        })
      }
    }, [open, triggerRef, align, sideOffset])

    if (!open || !mounted) return null

    const content = (
      <>
        {/* 遮罩层，点击关闭 */}
        <div className="fixed inset-0 z-[100]" onClick={() => onOpenChange(false)} />
        <div
          ref={(node) => {
            // 合并 refs
            (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node
            if (typeof ref === 'function') {
              ref(node)
            } else if (ref) {
              ref.current = node
            }
          }}
          className={cn(
            'fixed z-[101] min-w-[8rem] overflow-hidden rounded-md border border-slate-200 bg-white p-1 text-slate-900 shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
            className
          )}
          style={{
            top: position.top,
            left: position.left,
          }}
          {...props}
        >
          {children}
        </div>
      </>
    )

    // 使用 Portal 渲染到 body
    return createPortal(content, document.body)
  }
)
DropdownMenuContent.displayName = 'DropdownMenuContent'

// DropdownMenuItem
interface DropdownMenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean
  disabled?: boolean
  asChild?: boolean
}

const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ className, inset, disabled, onClick, asChild, children, ...props }, ref) => {
    const { onOpenChange } = React.useContext(DropdownMenuContext)

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return
      onClick?.(e)
      onOpenChange(false)
    }

    const itemClassName = cn(
      'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
      'transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900',
      disabled && 'pointer-events-none opacity-50',
      inset && 'pl-8',
      className
    )

    // 如果使用 asChild，将样式和事件附加到子元素
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<any>, {
        className: cn(itemClassName, (children as React.ReactElement<any>).props?.className),
        onClick: (e: React.MouseEvent) => {
          if (disabled) return
          ;(children as React.ReactElement<any>).props?.onClick?.(e)
          onOpenChange(false)
        },
      })
    }

    return (
      <div
        ref={ref}
        className={itemClassName}
        onClick={handleClick}
        {...props}
      >
        {children}
      </div>
    )
  }
)
DropdownMenuItem.displayName = 'DropdownMenuItem'

// DropdownMenuCheckboxItem
interface DropdownMenuCheckboxItemProps extends React.HTMLAttributes<HTMLDivElement> {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
}

const DropdownMenuCheckboxItem = React.forwardRef<HTMLDivElement, DropdownMenuCheckboxItemProps>(
  ({ className, children, checked, onCheckedChange, disabled, ...props }, ref) => {
    const { onOpenChange } = React.useContext(DropdownMenuContext)

    const handleClick = () => {
      if (disabled) return
      onCheckedChange?.(!checked)
      onOpenChange(false)
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
          'transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900',
          disabled && 'pointer-events-none opacity-50',
          className
        )}
        onClick={handleClick}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          {checked && <CheckOutlined style={{ fontSize: 12 }} />}
        </span>
        {children}
      </div>
    )
  }
)
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem'

// DropdownMenuRadioGroup
interface DropdownMenuRadioGroupProps {
  value?: string
  onValueChange?: (value: string) => void
  children?: React.ReactNode
}

const DropdownMenuRadioGroupContext = React.createContext<{
  value?: string
  onValueChange?: (value: string) => void
}>({})

function DropdownMenuRadioGroup({ value, onValueChange, children }: DropdownMenuRadioGroupProps) {
  return (
    <DropdownMenuRadioGroupContext.Provider value={{ value, onValueChange }}>
      {children}
    </DropdownMenuRadioGroupContext.Provider>
  )
}

// DropdownMenuRadioItem
interface DropdownMenuRadioItemProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  disabled?: boolean
}

const DropdownMenuRadioItem = React.forwardRef<HTMLDivElement, DropdownMenuRadioItemProps>(
  ({ className, children, value, disabled, ...props }, ref) => {
    const { value: selectedValue, onValueChange } = React.useContext(DropdownMenuRadioGroupContext)
    const { onOpenChange } = React.useContext(DropdownMenuContext)
    const isSelected = selectedValue === value

    const handleClick = () => {
      if (disabled) return
      onValueChange?.(value)
      onOpenChange(false)
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
          'transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:text-slate-900',
          disabled && 'pointer-events-none opacity-50',
          className
        )}
        onClick={handleClick}
        {...props}
      >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          {isSelected && <span className="h-2 w-2 rounded-full bg-current" />}
        </span>
        {children}
      </div>
    )
  }
)
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem'

// DropdownMenuLabel
interface DropdownMenuLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean
}

const DropdownMenuLabel = React.forwardRef<HTMLDivElement, DropdownMenuLabelProps>(
  ({ className, inset, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
      {...props}
    />
  )
)
DropdownMenuLabel.displayName = 'DropdownMenuLabel'

// DropdownMenuSeparator
const DropdownMenuSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('-mx-1 my-1 h-px bg-slate-200', className)} {...props} />
  )
)
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator'

// DropdownMenuShortcut
function DropdownMenuShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn('ml-auto text-xs tracking-widest opacity-60', className)} {...props} />
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

// DropdownMenuGroup
function DropdownMenuGroup({ children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div {...props}>{children}</div>
}

// DropdownMenuPortal
function DropdownMenuPortal({ children }: { children?: React.ReactNode }) {
  return <>{children}</>
}

// DropdownMenuSub（子菜单，简化实现）
function DropdownMenuSub({ children }: { children?: React.ReactNode }) {
  return <>{children}</>
}

// DropdownMenuSubTrigger
interface DropdownMenuSubTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  inset?: boolean
}

const DropdownMenuSubTrigger = React.forwardRef<HTMLDivElement, DropdownMenuSubTriggerProps>(
  ({ className, inset, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none',
        'hover:bg-slate-100 focus:bg-slate-100',
        inset && 'pl-8',
        className
      )}
      {...props}
    >
      {children}
      <RightOutlined className="ml-auto h-4 w-4" style={{ fontSize: 12 }} />
    </div>
  )
)
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger'

// DropdownMenuSubContent
const DropdownMenuSubContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'z-50 min-w-[8rem] overflow-hidden rounded-md border border-slate-200 bg-white p-1 text-slate-900 shadow-lg',
        className
      )}
      {...props}
    />
  )
)
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  // 导出 Ant Design Dropdown 以便直接使用
  Dropdown as AntDropdown,
}
